import { apiClient } from './api';
import { serverLogout } from './sessionRefresh';
import type {
  User,
  UserRole,
  LoginRequest,
  LoginResponse,
  RoleSummary,
  UserCreate,
  UserUpdate,
  UserListResponse,
  RoleListResponse,
} from '../types';

/**
 * Subtitle for a user option in a picker. Includes the username so the
 * dropdown search (which matches label + subtitle) finds people by login name.
 */
export function userPickerSubtitle(u: { username?: string | null; email?: string | null }): string {
  return [u.username, u.email].filter(Boolean).join(' · ');
}

class UserService {
  /**
   * Login user and store auth token
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // V17.534 (RBAC 2A): credentials so the browser keeps the httpOnly refresh cookie the API
    // (a different origin, same site) sets on login.
    const response = await apiClient.post<LoginResponse>('/users/login', credentials, { withCredentials: true });

    // Store token and user info
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('current_user', JSON.stringify(response.user));

    return response;
  }

  /**
   * Logout user
   */
  logout(): void {
    // V17.534 (RBAC 2A): end this login on the server too (best effort, never blocks).
    serverLogout();
    localStorage.removeItem('access_token');
    localStorage.removeItem('current_user');
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    return apiClient.get<User>('/users/me');
  }

  /**
   * Get current user from local storage
   */
  getLocalUser(): User | null {
    const userStr = localStorage.getItem('current_user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  /**
   * Check if current user has admin role
   */
  isAdmin(): boolean {
    const user = this.getLocalUser();
    return user?.role === 'admin' || user?.role === 'super_admin';
  }

  /**
   * Check if current user has approver role
   */
  isApprover(): boolean {
    const user = this.getLocalUser();
    return user?.role === 'approver' || user?.role === 'admin' || user?.role === 'super_admin';
  }

  /**
   * List all users (admin only)
   */
  async listUsers(params?: {
    skip?: number;
    limit?: number;
    role?: string;
    is_active?: boolean;
  }): Promise<UserListResponse> {
    return apiClient.get<UserListResponse>('/users', params);
  }

  /**
   * List every user matching the filters, paging past the endpoint's `limit`.
   *
   * The users endpoint returns at most `limit` rows (newest first). Pickers
   * that loaded a single limit=500 page silently dropped the oldest accounts
   * once there were more than 500 users.
   */
  async listAllUsers(params?: { role?: string; is_active?: boolean }): Promise<User[]> {
    const pageSize = 500;
    const all: User[] = [];
    for (let skip = 0; ; skip += pageSize) {
      const res = await this.listUsers({ ...params, skip, limit: pageSize });
      const page = res.users ?? [];
      all.push(...page);
      if (page.length < pageSize || all.length >= (res.total ?? 0)) break;
    }
    return all;
  }

  /**
   * Get user by ID (admin only)
   */
  async getUser(userId: string): Promise<User> {
    return apiClient.get<User>(`/users/${userId}`);
  }

  /**
   * Create new user (admin only)
   */
  async createUser(userData: UserCreate): Promise<User> {
    return apiClient.post<User>('/users', userData);
  }

  /**
   * Update user (admin only)
   */
  async updateUser(userId: string, userData: UserUpdate): Promise<User> {
    return apiClient.put<User>(`/users/${userId}`, userData);
  }

  /**
   * Update user role (admin only)
   *
   * V7.0 Dynamic RBAC - Uses role_id (UUID) instead of role enum
   * Dedicated endpoint for role changes with audit logging
   */
  async updateUserRole(userId: string, newRoleId: string): Promise<User> {
    return apiClient.put<User>(`/users/${userId}/role`, { role_id: newRoleId });
  }

  /**
   * Delete user (admin only)
   */
  async deleteUser(userId: string): Promise<void> {
    return apiClient.delete<void>(`/users/${userId}`);
  }

  /**
   * V17.71: Select a role for multi-role users.
   * Issues a full JWT with the selected role's permissions.
   */
  async selectRole(roleId: string): Promise<LoginResponse> {
    // V17.534 (RBAC 2A, D2): send the refresh cookie so this login's renewals use the chosen role.
    const response = await apiClient.post<LoginResponse>(
      `/users/select-role?role_id=${roleId}`, undefined, { withCredentials: true }
    );

    // Replace limited token with full token
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('current_user', JSON.stringify(response.user));

    return response;
  }

  /**
   * V17.71: Get all roles assigned to the current user.
   * Used by the role switcher dropdown in the header.
   */
  async getMyRoles(): Promise<RoleSummary[]> {
    return apiClient.get<RoleSummary[]>('/users/me/roles');
  }

  /**
   * V7.0 Dynamic RBAC - Get all roles (admin only)
   *
   * Fetches all available roles from the database to populate role dropdowns
   * in the Create User and Edit User modals.
   */
  async getRoles(): Promise<RoleListResponse> {
    return apiClient.get<RoleListResponse>('/admin/roles');
  }
}

export const userService = new UserService();
